import { Rng } from '../core/rng';

/**
 * Synthesis toolkit shared by the SFX library, the music instruments and the
 * ambience beds. Nothing here loads audio files: every sound is generated.
 *
 * - Pure DSP generators (noise, reverb impulse responses, Karplus–Strong
 *   strings, waveshaper curves) are plain functions over Float32Arrays so they
 *   can be unit-tested in Node without Web Audio.
 * - `Synth` is a small voice builder bound to one BaseAudioContext. Recipes
 *   describe layered one-shots (tones, filtered noise bursts, FM, additive
 *   partials, debris grains) with time offsets relative to the voice start.
 *   The builder applies pitch/time scaling, tracks every node for cleanup and
 *   guarantees that every source it starts is also stopped.
 */

export type NoiseColor = 'white' | 'pink' | 'brown';
/** Float32Array backed by a plain ArrayBuffer (the type Web Audio APIs accept). */
export type F32 = Float32Array<ArrayBuffer>;

export const midiToFreq = (midi: number): number => 440 * 2 ** ((midi - 69) / 12);
export const freqToMidi = (freq: number): number => 69 + 12 * Math.log2(freq / 440);
export const dbToGain = (db: number): number => 10 ** (db / 20);
export const gainToDb = (gain: number): number => 20 * Math.log10(Math.max(1e-9, gain));

/** Keeps oscillator/filter frequencies inside the range browsers accept. */
export function clampFreq(f: number, max = 20000): number {
  if (!Number.isFinite(f)) return 440;
  return Math.min(max, Math.max(1, f));
}

/** Scales `data` in place so its absolute peak equals `peak`. */
export function normalizePeak(data: Float32Array, peak = 0.9): void {
  let m = 0;
  for (let i = 0; i < data.length; i++) {
    const v = Math.abs(data[i]!);
    if (v > m) m = v;
  }
  if (m <= 0) return;
  const k = peak / m;
  for (let i = 0; i < data.length; i++) data[i] = data[i]! * k;
}

// ---------------------------------------------------------------- pure generators

/**
 * Seamlessly loopable noise of the given colour. `length` samples; the tail is
 * crossfaded into the head so looping sources never click.
 */
export function generateNoise(color: NoiseColor, length: number, seed = 1): F32 {
  const fade = Math.min(4096, Math.floor(length / 4));
  const raw = new Float32Array(length + fade);
  const rng = new Rng(seed);
  if (color === 'white') {
    for (let i = 0; i < raw.length; i++) raw[i] = rng.next() * 2 - 1;
  } else if (color === 'pink') {
    // Paul Kellet's refined pink filter.
    let b0 = 0;
    let b1 = 0;
    let b2 = 0;
    let b3 = 0;
    let b4 = 0;
    let b5 = 0;
    let b6 = 0;
    for (let i = 0; i < raw.length; i++) {
      const w = rng.next() * 2 - 1;
      b0 = 0.99886 * b0 + w * 0.0555179;
      b1 = 0.99332 * b1 + w * 0.0750759;
      b2 = 0.969 * b2 + w * 0.153852;
      b3 = 0.8665 * b3 + w * 0.3104856;
      b4 = 0.55 * b4 + w * 0.5329522;
      b5 = -0.7616 * b5 - w * 0.016898;
      raw[i] = b0 + b1 + b2 + b3 + b4 + b5 + b6 + w * 0.5362;
      b6 = w * 0.115926;
    }
  } else {
    // Leaky integrator ("brown"): strong lows, bounded, no DC drift.
    let last = 0;
    for (let i = 0; i < raw.length; i++) {
      last = (last + 0.02 * (rng.next() * 2 - 1)) / 1.02;
      raw[i] = last;
    }
  }
  const out = new Float32Array(length);
  for (let i = 0; i < length; i++) {
    const v = raw[i + fade]!;
    const k = i - (length - fade);
    if (k >= 0) {
      const a = k / fade;
      out[i] = v * (1 - a) + raw[k]! * a;
    } else {
      out[i] = v;
    }
  }
  normalizePeak(out, 0.95);
  return out;
}

export interface ImpulseOptions {
  /** Length of the tail in seconds. */
  seconds?: number;
  /** Power-law decay exponent: higher = shorter perceived tail. */
  decay?: number;
  /** Silence before the first reflection. */
  preDelay?: number;
  /** 0..1: how quickly highs die compared to lows (darker tails). */
  damping?: number;
  /** Level of the sparse early reflections. */
  early?: number;
  seed?: number;
}

/**
 * Stereo room/hall impulse response: sparse early reflections followed by a
 * decorrelated noise tail that gets darker as it decays (air absorption).
 */
export function generateImpulseResponse(sampleRate: number, o: ImpulseOptions = {}): [F32, F32] {
  const seconds = o.seconds ?? 2.4;
  const decay = o.decay ?? 3;
  const pre = o.preDelay ?? 0.012;
  const damping = o.damping ?? 0.65;
  const early = o.early ?? 0.5;
  const len = Math.max(16, Math.floor(sampleRate * seconds));
  const preN = Math.min(len - 8, Math.floor(pre * sampleRate));
  const chans: F32[] = [];
  for (let ch = 0; ch < 2; ch++) {
    const rng = new Rng((o.seed ?? 7) * 31 + ch * 977);
    const d = new Float32Array(len);
    let lp = 0;
    const span = len - preN;
    for (let i = preN; i < len; i++) {
      const t = (i - preN) / span;
      const env = (1 - t) ** decay;
      const coef = Math.min(0.96, damping * t * 1.2);
      lp = lp * coef + (rng.next() * 2 - 1) * (1 - coef);
      // Compensate the level lost to the one-pole filter so darkening is spectral, not a drop.
      d[i] = lp * env * (1 + coef * 1.5);
    }
    for (let k = 0; k < 12; k++) {
      const time = pre + 0.003 + rng.next() * 0.075;
      const idx = Math.min(len - 1, Math.floor(time * sampleRate));
      d[idx] = d[idx]! + (rng.next() < 0.5 ? -1 : 1) * early * (1 - k / 14);
    }
    chans.push(d);
  }
  // Joint normalisation keeps the stereo balance.
  let m = 0;
  for (const c of chans) for (let i = 0; i < c.length; i++) m = Math.max(m, Math.abs(c[i]!));
  const k = m > 0 ? 0.9 / m : 1;
  for (const c of chans) for (let i = 0; i < c.length; i++) c[i] = c[i]! * k;
  return [chans[0]!, chans[1]!];
}

export interface PluckOptions {
  /** Buffer length in seconds. */
  seconds?: number;
  /** Time for the fundamental to fall by 60 dB. */
  t60?: number;
  /** 0..1 excitation brightness (lowpass on the initial noise burst). */
  brightness?: number;
  seed?: number;
}

/**
 * Karplus–Strong plucked string. Returns the samples and the true pitch of the
 * integer delay line (sampleRate / (N + 0.5)) so callers can correct tuning
 * with `playbackRate`.
 */
export function karplusStrong(
  sampleRate: number,
  freq: number,
  o: PluckOptions = {},
): { data: F32; freq: number } {
  const t60 = Math.max(0.05, o.t60 ?? 1.2);
  const seconds = o.seconds ?? Math.min(3, t60 * 1.05);
  const len = Math.max(64, Math.floor(sampleRate * seconds));
  const period = Math.max(2, Math.round(sampleRate / Math.max(20, freq) - 0.5));
  const bright = Math.min(1, Math.max(0.03, o.brightness ?? 0.6));
  const loss = 10 ** ((-3 * period) / (sampleRate * t60));
  const rng = new Rng(o.seed ?? 3);
  const out = new Float32Array(len);
  let lp = 0;
  let mean = 0;
  const n0 = Math.min(period, len);
  for (let i = 0; i < n0; i++) {
    lp += (rng.next() * 2 - 1 - lp) * bright;
    out[i] = lp;
    mean += lp;
  }
  mean /= n0;
  for (let i = 0; i < n0; i++) out[i] = out[i]! - mean;
  for (let i = period; i < len; i++) {
    const a = out[i - period]!;
    const b = i - period - 1 >= 0 ? out[i - period - 1]! : 0;
    out[i] = loss * 0.5 * (a + b);
  }
  // Short fade at the end so the buffer never stops on a non-zero sample.
  const fade = Math.min(len, Math.floor(sampleRate * 0.02));
  for (let i = 0; i < fade; i++) out[len - 1 - i] = out[len - 1 - i]! * (i / fade);
  normalizePeak(out, 0.9);
  return { data: out, freq: sampleRate / (period + 0.5) };
}

/** Identity below `knee`, smooth saturation above: a last-resort clip guard. */
export function softClipCurve(samples = 2048, knee = 0.8): F32 {
  const c = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    const ax = Math.abs(x);
    const y = ax <= knee ? ax : knee + (1 - knee) * Math.tanh((ax - knee) / (1 - knee));
    c[i] = Math.sign(x) * y;
  }
  return c;
}

const curveCache = new Map<string, F32>();

/** tanh drive curve (gain-compensated) for grit on roars and explosions. */
export function driveCurve(amount: number, samples = 1024): F32 {
  const key = `${amount.toFixed(2)}:${samples}`;
  const hit = curveCache.get(key);
  if (hit) return hit;
  const c = new Float32Array(samples);
  const norm = Math.tanh(amount);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    c[i] = Math.tanh(amount * x) / norm;
  }
  curveCache.set(key, c);
  return c;
}

// ---------------------------------------------------------------- context-bound caches

const NOISE_SECONDS = 4;
const noiseCache = new WeakMap<BaseAudioContext, Partial<Record<NoiseColor, AudioBuffer>>>();

/** Loopable mono noise buffer, generated once per context and colour. */
export function noiseBuffer(ctx: BaseAudioContext, color: NoiseColor): AudioBuffer {
  let c = noiseCache.get(ctx);
  if (!c) {
    c = {};
    noiseCache.set(ctx, c);
  }
  const hit = c[color];
  if (hit) return hit;
  const len = Math.floor(ctx.sampleRate * NOISE_SECONDS);
  const b = ctx.createBuffer(1, len, ctx.sampleRate);
  b.getChannelData(0).set(
    generateNoise(color, len, color === 'white' ? 11 : color === 'pink' ? 23 : 37),
  );
  c[color] = b;
  return b;
}

export type PluckKind = 'harp' | 'oud' | 'bow' | 'music';
interface PluckSpec {
  t60: (freq: number) => number;
  brightness: number;
}
const PLUCKS: Record<PluckKind, PluckSpec> = {
  harp: { t60: (f) => Math.min(2.2, Math.max(0.8, 2.6 - f / 900)), brightness: 0.55 },
  oud: { t60: (f) => Math.min(1.3, Math.max(0.5, 1.5 - f / 1200)), brightness: 0.35 },
  bow: { t60: () => 0.35, brightness: 0.8 },
  music: { t60: () => 0.9, brightness: 0.7 },
};
const pluckCache = new WeakMap<
  BaseAudioContext,
  Map<string, { buffer: AudioBuffer; freq: number }>
>();

/**
 * Karplus–Strong buffer for a pitch. Buffers are generated every 3 semitones
 * and re-pitched with `playbackRate`, which keeps memory small.
 */
export function pluckBuffer(
  ctx: BaseAudioContext,
  midi: number,
  kind: PluckKind,
): { buffer: AudioBuffer; rate: number } {
  let cache = pluckCache.get(ctx);
  if (!cache) {
    cache = new Map();
    pluckCache.set(ctx, cache);
  }
  const base = Math.round(midi / 3) * 3;
  const key = `${kind}:${base}`;
  let entry = cache.get(key);
  if (!entry) {
    const spec = PLUCKS[kind];
    const f = midiToFreq(base);
    // Plucks are dark enough that 32 kHz loses nothing and halves the memory.
    const sr = Math.min(ctx.sampleRate, 32000);
    const ks = karplusStrong(sr, f, {
      t60: spec.t60(f),
      brightness: spec.brightness,
      seed: base * 13 + kind.length,
    });
    const buffer = ctx.createBuffer(1, ks.data.length, sr);
    buffer.getChannelData(0).set(ks.data);
    entry = { buffer, freq: ks.freq };
    cache.set(key, entry);
  }
  return { buffer: entry.buffer, rate: midiToFreq(midi) / entry.freq };
}

// ---------------------------------------------------------------- voice builder

export interface FilterSpec {
  type?: BiquadFilterType;
  freq: number;
  q?: number;
  gain?: number;
  /** Sweep target and duration (from the owning layer's start). */
  to?: number;
  sweep?: number;
  /** Multi-point sweep: [offset from layer start, frequency]. */
  path?: readonly (readonly [number, number])[];
  curve?: 'exp' | 'lin';
}

export interface EnvSpec {
  /** Start offset in seconds. */
  at?: number;
  attack?: number;
  hold?: number;
  /** Time from the end of hold to (near) silence. */
  decay: number;
  peak?: number;
}

export interface LfoSpec {
  rate: number;
  /** Hz for 'freq', cents for 'detune', 0..1 for 'amp'. */
  depth: number;
  type?: OscillatorType;
  target?: 'freq' | 'detune' | 'amp';
}

export interface ToneSpec extends EnvSpec {
  type?: OscillatorType;
  wave?: PeriodicWave;
  freq: number;
  to?: number;
  sweep?: number;
  path?: readonly (readonly [number, number])[];
  curve?: 'exp' | 'lin';
  detune?: number;
  filter?: FilterSpec;
  lfo?: LfoSpec;
  dest?: AudioNode;
}

export interface BurstSpec extends EnvSpec {
  color?: NoiseColor;
  filter?: FilterSpec;
  filter2?: FilterSpec;
  rate?: number;
  lfo?: LfoSpec;
  drive?: number;
  dest?: AudioNode;
}

export interface FmSpec extends EnvSpec {
  freq: number;
  to?: number;
  sweep?: number;
  ratio: number;
  /** Modulation index (depth in multiples of the modulator frequency). */
  index: number;
  indexTo?: number;
  indexTime?: number;
  type?: OscillatorType;
  modType?: OscillatorType;
  filter?: FilterSpec;
  dest?: AudioNode;
}

export interface PartialsSpec extends EnvSpec {
  freq: number;
  ratios: readonly number[];
  amps?: readonly number[];
  decays?: readonly number[];
  attacks?: readonly number[];
  /** Random detune spread in cents per partial. */
  spread?: number;
  type?: OscillatorType;
  filter?: FilterSpec;
  dest?: AudioNode;
}

export interface GrainSpec {
  count: number;
  at?: number;
  /** Grains are scattered over this many seconds. */
  spread: number;
  freq: readonly [number, number];
  decay: readonly [number, number];
  peak: number;
  q?: number;
  /** Exponent for the amplitude falloff across the spread (0 = none). */
  falloff?: number;
  color?: NoiseColor;
  dest?: AudioNode;
}

export type Vowel = 'ah' | 'oh' | 'oo' | 'ee';
/** Formant centre frequency, Q and relative gain for a sung vowel. */
export const VOWELS: Record<Vowel, readonly (readonly [number, number, number])[]> = {
  ah: [
    [750, 7, 1],
    [1150, 9, 0.55],
    [2600, 12, 0.22],
  ],
  oh: [
    [480, 7, 1],
    [850, 9, 0.5],
    [2700, 12, 0.14],
  ],
  oo: [
    [330, 6, 1],
    [860, 9, 0.28],
    [2300, 12, 0.08],
  ],
  ee: [
    [300, 7, 1],
    [2250, 12, 0.35],
    [3000, 14, 0.2],
  ],
};

export interface SynthOptions {
  /** Frequency multiplier applied to every oscillator and filter. */
  pitch?: number;
  /** Time multiplier applied to every offset/envelope. */
  stretch?: number;
  rand?: () => number;
  /** Recipe-specific flavour (element, creature, ...). */
  variant?: string;
  /** Recipe-specific duration hint in seconds (0 = none). */
  hint?: number;
}

/**
 * Builds one voice. All times given to the helpers are offsets in seconds from
 * the voice start (`t0`); `end` tracks when the last source stops.
 */
export class Synth {
  readonly nodes: AudioNode[] = [];
  readonly sources: AudioScheduledSourceNode[] = [];
  /** Latest scheduled stop, in real seconds after `t0`. */
  end = 0;
  readonly pitch: number;
  readonly stretch: number;
  readonly rand: () => number;
  readonly variant: string;
  readonly hint: number;

  constructor(
    readonly ctx: BaseAudioContext,
    readonly out: AudioNode,
    readonly t0: number,
    o: SynthOptions = {},
  ) {
    this.pitch = o.pitch ?? 1;
    this.stretch = o.stretch ?? 1;
    this.rand = o.rand ?? Math.random;
    this.variant = o.variant ?? '';
    this.hint = o.hint ?? 0;
  }

  /** Absolute context time of an offset. */
  time(off: number): number {
    return this.t0 + Math.max(0, off) * this.stretch;
  }

  /** Pitch-scaled, clamped frequency. */
  hz(f: number): number {
    return clampFreq(f * this.pitch, this.ctx.sampleRate * 0.45);
  }

  r(lo: number, hi: number): number {
    return lo + (hi - lo) * this.rand();
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.min(items.length - 1, Math.floor(this.rand() * items.length))] as T;
  }

  private mark(off: number): void {
    this.end = Math.max(this.end, off * this.stretch);
  }

  // ---------------------------------------------------------- node factories

  gain(value = 1, dest: AudioNode | null = this.out): GainNode {
    const g = this.ctx.createGain();
    g.gain.value = value;
    if (dest) g.connect(dest);
    this.nodes.push(g);
    return g;
  }

  filter(
    type: BiquadFilterType,
    freq: number,
    q = 0.707,
    dest: AudioNode | null = this.out,
  ): BiquadFilterNode {
    const f = this.ctx.createBiquadFilter();
    f.type = type;
    f.frequency.value = this.hz(freq);
    f.Q.value = q;
    if (dest) f.connect(dest);
    this.nodes.push(f);
    return f;
  }

  shaper(curve: F32, dest: AudioNode | null = this.out): WaveShaperNode {
    const w = this.ctx.createWaveShaper();
    w.curve = curve;
    if (dest) w.connect(dest);
    this.nodes.push(w);
    return w;
  }

  /** Stereo placement inside the voice (falls back to a plain gain). */
  panner(pan: number, dest: AudioNode | null = this.out): AudioNode {
    if (typeof this.ctx.createStereoPanner !== 'function') return this.gain(1, dest);
    const p = this.ctx.createStereoPanner();
    p.pan.value = Math.max(-1, Math.min(1, pan));
    if (dest) p.connect(dest);
    this.nodes.push(p);
    return p;
  }

  private startStop(src: AudioScheduledSourceNode, start: number, stop: number): void {
    const s = Math.max(0, start);
    src.start(this.time(s));
    this.scheduleStop(src, s, stop);
  }

  private scheduleStop(src: AudioScheduledSourceNode, start: number, stop: number): void {
    const e = Math.max(stop, start + 0.01);
    src.stop(this.time(e));
    this.sources.push(src);
    this.nodes.push(src);
    this.mark(e);
  }

  osc(
    type: OscillatorType,
    freq: number,
    start: number,
    stop: number,
    dest: AudioNode | null,
    detune = 0,
    wave?: PeriodicWave,
  ): OscillatorNode {
    const o = this.ctx.createOscillator();
    if (wave) o.setPeriodicWave(wave);
    else o.type = type;
    o.frequency.value = this.hz(freq);
    if (detune) o.detune.value = detune;
    if (dest) o.connect(dest);
    this.startStop(o, start, stop);
    return o;
  }

  noise(
    color: NoiseColor,
    start: number,
    stop: number,
    dest: AudioNode | null,
    rate = 1,
  ): AudioBufferSourceNode {
    const b = noiseBuffer(this.ctx, color);
    const src = this.ctx.createBufferSource();
    src.buffer = b;
    src.loop = true;
    src.playbackRate.value = Math.max(0.05, rate);
    if (dest) src.connect(dest);
    const s = Math.max(0, start);
    src.start(this.time(s), this.rand() * Math.max(0, b.duration - 0.1));
    this.scheduleStop(src, s, stop);
    return src;
  }

  // ---------------------------------------------------------- automation

  /**
   * Automates `param` from `from` through `path` points ([offset, value],
   * offsets relative to `start`). Frequencies are pitch-scaled when `isFreq`.
   */
  automate(
    param: AudioParam,
    start: number,
    from: number,
    path: readonly (readonly [number, number])[],
    curve: 'exp' | 'lin' = 'exp',
    isFreq = true,
  ): void {
    const conv = (v: number): number => (isFreq ? this.hz(v) : v);
    const v0 = curve === 'exp' ? Math.max(1e-4, conv(from)) : conv(from);
    param.setValueAtTime(v0, this.time(start));
    let last = 0;
    for (const [t, v] of path) {
      const tt = Math.max(last + 0.001, t);
      last = tt;
      if (curve === 'exp')
        param.exponentialRampToValueAtTime(Math.max(1e-4, conv(v)), this.time(start + tt));
      else param.linearRampToValueAtTime(conv(v), this.time(start + tt));
    }
  }

  sweep(
    param: AudioParam,
    start: number,
    from: number,
    to: number,
    dur: number,
    curve: 'exp' | 'lin' = 'exp',
  ): void {
    this.automate(param, start, from, [[Math.max(0.002, dur), to]], curve, true);
  }

  /** Attack–hold–decay on a gain param. Returns the offset of (near) silence. */
  env(param: AudioParam, e: EnvSpec): number {
    const at = e.at ?? 0;
    const a = Math.max(0.0005, e.attack ?? 0.002);
    const h = Math.max(0, e.hold ?? 0);
    const d = Math.max(0.004, e.decay);
    const peak = e.peak ?? 0.5;
    const t = this.time(at);
    param.setValueAtTime(0, t);
    param.linearRampToValueAtTime(peak, t + a * this.stretch);
    if (h > 0) param.setValueAtTime(peak, t + (a + h) * this.stretch);
    param.setTargetAtTime(0, t + (a + h) * this.stretch, (d * this.stretch) / 6);
    return at + a + h + d;
  }

  applyFilter(spec: FilterSpec, at: number, dest: AudioNode): BiquadFilterNode {
    const f = this.filter(spec.type ?? 'lowpass', spec.freq, spec.q ?? 0.707, dest);
    if (spec.gain !== undefined) f.gain.value = spec.gain;
    if (spec.path) this.automate(f.frequency, at, spec.freq, spec.path, spec.curve ?? 'exp', true);
    else if (spec.to !== undefined)
      this.sweep(f.frequency, at, spec.freq, spec.to, spec.sweep ?? 0.2, spec.curve ?? 'exp');
    return f;
  }

  /** Connects an LFO to `param` between `start` and `stop`. */
  lfo(spec: LfoSpec, start: number, stop: number, param: AudioParam): void {
    const depth = spec.target === 'freq' ? spec.depth * this.pitch : spec.depth;
    const g = this.gain(depth, null);
    g.connect(param);
    const o = this.ctx.createOscillator();
    o.type = spec.type ?? 'sine';
    o.frequency.value = Math.max(0.01, spec.rate);
    o.connect(g);
    this.startStop(o, start, stop);
  }

  /** Inserts a tremolo stage (gain oscillating between 1-depth and 1). */
  private tremolo(spec: LfoSpec, start: number, stop: number, dest: AudioNode): AudioNode {
    const d = Math.max(0, Math.min(1, spec.depth));
    const trem = this.gain(1 - d / 2, dest);
    this.lfo({ ...spec, depth: d / 2, target: 'amp' }, start, stop, trem.gain);
    return trem;
  }

  // ---------------------------------------------------------- layers

  /** Oscillator layer with envelope, optional sweep/path, filter and LFO. */
  tone(s: ToneSpec): OscillatorNode {
    const at = s.at ?? 0;
    const end =
      at +
      Math.max(0.0005, s.attack ?? 0.002) +
      Math.max(0, s.hold ?? 0) +
      Math.max(0.004, s.decay) +
      0.02;
    let dest = s.dest ?? this.out;
    if (s.lfo?.target === 'amp') dest = this.tremolo(s.lfo, at, end, dest);
    if (s.filter) dest = this.applyFilter(s.filter, at, dest);
    const g = this.gain(0, dest);
    this.env(g.gain, s);
    const o = this.osc(s.type ?? 'sine', s.freq, at, end, g, s.detune ?? 0, s.wave);
    if (s.path) this.automate(o.frequency, at, s.freq, s.path, s.curve ?? 'exp', true);
    else if (s.to !== undefined)
      this.sweep(o.frequency, at, s.freq, s.to, s.sweep ?? end - at, s.curve ?? 'exp');
    if (s.lfo && s.lfo.target !== 'amp')
      this.lfo(s.lfo, at, end, s.lfo.target === 'detune' ? o.detune : o.frequency);
    return o;
  }

  /** Filtered noise layer with envelope. */
  burst(s: BurstSpec): AudioBufferSourceNode {
    const at = s.at ?? 0;
    const end =
      at +
      Math.max(0.0005, s.attack ?? 0.002) +
      Math.max(0, s.hold ?? 0) +
      Math.max(0.004, s.decay) +
      0.02;
    let dest = s.dest ?? this.out;
    if (s.lfo) dest = this.tremolo(s.lfo, at, end, dest);
    const g = this.gain(0, dest);
    this.env(g.gain, s);
    let head: AudioNode = g;
    if (s.drive) head = this.shaper(driveCurve(s.drive), head);
    if (s.filter2) head = this.applyFilter(s.filter2, at, head);
    if (s.filter) head = this.applyFilter(s.filter, at, head);
    return this.noise(s.color ?? 'white', at, end, head, s.rate ?? 1);
  }

  /** Two-operator FM (carrier + modulator) with a decaying index. */
  fm(s: FmSpec): OscillatorNode {
    const at = s.at ?? 0;
    const end =
      at +
      Math.max(0.0005, s.attack ?? 0.002) +
      Math.max(0, s.hold ?? 0) +
      Math.max(0.004, s.decay) +
      0.02;
    let dest = s.dest ?? this.out;
    if (s.filter) dest = this.applyFilter(s.filter, at, dest);
    const g = this.gain(0, dest);
    this.env(g.gain, s);
    const car = this.osc(s.type ?? 'sine', s.freq, at, end, g);
    const modFreq = s.freq * s.ratio;
    const mod = this.osc(s.modType ?? 'sine', modFreq, at, end, null);
    const mg = this.gain(0, null);
    mod.connect(mg);
    mg.connect(car.frequency);
    const t = this.time(at);
    mg.gain.setValueAtTime(s.index * this.hz(modFreq), t);
    if (s.indexTo !== undefined) {
      const tc = Math.max(0.005, (s.indexTime ?? (end - at) / 4) * this.stretch);
      mg.gain.setTargetAtTime(s.indexTo * this.hz(modFreq), t, tc);
    }
    if (s.to !== undefined) {
      const dur = s.sweep ?? end - at;
      this.sweep(car.frequency, at, s.freq, s.to, dur);
      this.sweep(mod.frequency, at, modFreq, s.to * s.ratio, dur);
    }
    return car;
  }

  /** Additive inharmonic partials (bells, gongs, metal). */
  partials(s: PartialsSpec): void {
    const at = s.at ?? 0;
    let dest = s.dest ?? this.out;
    if (s.filter) dest = this.applyFilter(s.filter, at, dest);
    const peak = s.peak ?? 0.5;
    s.ratios.forEach((ratio, i) => {
      this.tone({
        type: s.type ?? 'sine',
        freq: s.freq * ratio,
        at,
        attack: s.attacks?.[i] ?? s.attack ?? 0.002,
        hold: s.hold,
        decay: s.decays?.[i] ?? s.decay / (1 + i * 0.4),
        peak: (s.amps?.[i] ?? 1 / (i + 1)) * peak,
        detune: s.spread ? (this.rand() * 2 - 1) * s.spread : 0,
        dest,
      });
    });
  }

  /** Scattered short filtered-noise ticks: debris, crackle, shards. */
  grains(s: GrainSpec): void {
    const at0 = s.at ?? 0;
    for (let i = 0; i < s.count; i++) {
      const u = this.rand();
      const fall = s.falloff ? (1 - u) ** s.falloff : 1;
      this.burst({
        color: s.color ?? 'white',
        at: at0 + u * s.spread,
        attack: 0.0008,
        decay: this.r(s.decay[0], s.decay[1]),
        peak: s.peak * this.r(0.5, 1) * fall,
        filter: { type: 'bandpass', freq: this.r(s.freq[0], s.freq[1]), q: s.q ?? 2.5 },
        dest: s.dest,
      });
    }
  }

  /** 1–15 ms broadband transient. */
  click(at = 0, peak = 0.4, freq = 3000, dest?: AudioNode): void {
    this.burst({
      color: 'white',
      at,
      attack: 0.0005,
      decay: 0.012,
      peak,
      filter: { type: 'highpass', freq },
      dest,
    });
  }

  /** Sub "body" thump: a fast downward sine sweep. */
  thump(
    o: {
      at?: number;
      freq?: number;
      to?: number;
      decay?: number;
      peak?: number;
      dest?: AudioNode;
    } = {},
  ): void {
    const decay = o.decay ?? 0.15;
    this.tone({
      type: 'sine',
      freq: o.freq ?? 120,
      to: o.to ?? 45,
      sweep: decay * 0.8,
      at: o.at,
      attack: 0.0015,
      decay,
      peak: o.peak ?? 0.8,
      dest: o.dest,
    });
  }

  /** Karplus–Strong plucked string. */
  pluck(o: { freq: number; at?: number; peak?: number; kind?: PluckKind; dest?: AudioNode }): void {
    const at = o.at ?? 0;
    const midi = freqToMidi(this.hz(o.freq));
    const { buffer, rate } = pluckBuffer(this.ctx, midi, o.kind ?? 'music');
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    src.playbackRate.value = rate;
    const g = this.gain(o.peak ?? 0.5, o.dest ?? this.out);
    src.connect(g);
    // `pluckBuffer` already applied the pitch; the duration is the buffer's.
    this.startStop(src, at, at + buffer.duration / rate / this.stretch);
  }

  /** Parallel formant filter bank (shared by all notes of a choir chord). */
  formants(vowel: Vowel, dest: AudioNode = this.out): AudioNode {
    const input = this.gain(1, null);
    for (const [f, q, g] of VOWELS[vowel]) {
      const out = this.gain(g * 1.6, dest);
      const bp = this.ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = f;
      bp.Q.value = q;
      bp.connect(out);
      input.connect(bp);
      this.nodes.push(bp);
    }
    // A little unfiltered body keeps the chord from sounding thin.
    const body = this.filter('lowpass', 900, 0.7, this.gain(0.18, dest));
    input.connect(body);
    return input;
  }

  /** Sung chord swell ("aah") for epic stingers. `freqs` in Hz. */
  choir(o: {
    freqs: readonly number[];
    at?: number;
    attack: number;
    hold?: number;
    decay: number;
    peak: number;
    vowel?: Vowel;
    dest?: AudioNode;
  }): void {
    const bank = this.formants(o.vowel ?? 'ah', o.dest ?? this.out);
    for (const f of o.freqs) {
      for (const det of [-9, 8]) {
        this.tone({
          type: 'sawtooth',
          freq: f,
          detune: det + this.r(-3, 3),
          at: o.at,
          attack: o.attack,
          hold: o.hold,
          decay: o.decay,
          peak: o.peak,
          dest: bank,
          lfo: { rate: this.r(4.6, 5.6), depth: 14, target: 'detune' },
        });
      }
    }
  }

  /** Brass-like note: detuned saws through an opening lowpass with a pitch scoop. */
  brass(o: {
    freq: number;
    at?: number;
    dur: number;
    peak: number;
    bright?: number;
    dest?: AudioNode;
  }): void {
    const at = o.at ?? 0;
    const bright = o.bright ?? 1;
    const lp = this.filter('lowpass', o.freq * 1.2, 1.4, o.dest ?? this.out);
    this.automate(lp.frequency, at, o.freq * 1.2, [
      [0.06, o.freq * 6 * bright],
      [0.35, o.freq * 3.2 * bright],
    ]);
    for (const det of [-7, 7]) {
      const osc = this.tone({
        type: 'sawtooth',
        freq: o.freq,
        detune: det,
        at,
        attack: 0.035,
        hold: Math.max(0, o.dur - 0.035),
        decay: 0.28,
        peak: o.peak,
        dest: lp,
      });
      osc.detune.setValueAtTime(det - 45, this.time(at));
      osc.detune.linearRampToValueAtTime(det, this.time(at + 0.07));
    }
  }
}
